import {BaseService} from './BaseService';
import {ItemPaymentDto, PaymentStatisticsDto, PaymentTypeEnum} from '@joyrideautos/auction-core/src/dtos/PaymentsDto';
import {QuerySnapshot} from '../firebase/Firestore';
import {WithKey} from '../firebase/types';
import {toArrayWithKey} from '../utils';

export class ItemPaymentService extends BaseService {
    /*
{
      "collectionGroup": "paymentResults",
      "queryScope": "COLLECTION_GROUP",
      "fields": [
        { "fieldPath": "uid", "order": "ASCENDING" },
        { "fieldPath": "timestamp", "order": "DESCENDING" }
      ]
    }
 */
    subscribeToPaymentResults(uid: string, listener: (payments: ItemPaymentDto[]) => void): () => void {
        const paymentResultsQuery = this.firebase.firestore
            .collectionGroupRef<ItemPaymentDto>('paymentResults')
            .where('uid', '==', uid)
            .orderBy('timestamp', 'desc');
        return this.firebase.firestore.subscribeToCollection<ItemPaymentDto>(paymentResultsQuery, (result) =>
            listener(Object.values(result || {}))
        );
    }

    /*
    {
          "collectionGroup": "paymentResults",
          "queryScope": "COLLECTION_GROUP",
          "fields": [
            { "fieldPath": "auctionId", "order": "ASCENDING" },
            { "fieldPath": "itemId", "order": "ASCENDING" },
            { "fieldPath": "regionId", "order": "ASCENDING" },
            { "fieldPath": "uid", "order": "ASCENDING" }
          ]
        }
     */
    subscribeToPaymentResult(
        uid: string,
        regionId: string,
        auctionId: string,
        itemId: string,
        listener: (payment: ItemPaymentDto) => void
    ): () => void {
        const onSnapshot = (paymentSnapshot?: QuerySnapshot<ItemPaymentDto>) => {
            if (!paymentSnapshot || paymentSnapshot.empty) {
                return;
            }
            console.warn('payment results found', paymentSnapshot.size);
            listener({...paymentSnapshot.docs[0].data()});
        };

        const paymentResultsQuery = this.firebase.firestore
            .collectionGroupRef<ItemPaymentDto>('paymentResults')
            .orderBy('timestamp', 'desc')
            .where('uid', '==', uid)
            .where('regionId', '==', regionId)
            .where('auctionId', '==', auctionId)
            .where('itemId', '==', itemId);
        return this.firebase.firestore.subscribeToCollectionSnap<ItemPaymentDto>(paymentResultsQuery, onSnapshot);
    }

    async fetchPaymentResult(uid: string, regionId: string, auctionId: string, itemId: string, type: PaymentTypeEnum) {
        const paymentResultsQuery = this.firebase.firestore
            .collectionGroupRef<ItemPaymentDto>('paymentResults')
            .orderBy('timestamp', 'desc')
            .where('uid', '==', uid)
            .where('regionId', '==', regionId)
            .where('auctionId', '==', auctionId)
            .where('itemId', '==', itemId)
            .where('type', '==', type);

        const snapshots = await this.firebase.firestore.fetchOnceDocuments(paymentResultsQuery);
        return snapshots && snapshots[0] ? snapshots[0].data() : null;
    }

    async fetchPaymentResults(
        uid: string,
        regionId: string,
        auctionId: string,
        itemId: string,
        type?: PaymentTypeEnum | null
    ): Promise<ItemPaymentDto[]> {
        let paymentResultsQuery = this.firebase.firestore
            .collectionGroupRef<ItemPaymentDto>('paymentResults')
            .orderBy('timestamp', 'desc')
            .where('uid', '==', uid)
            .where('regionId', '==', regionId)
            .where('auctionId', '==', auctionId)
            .where('itemId', '==', itemId);
        if (type) {
            paymentResultsQuery = paymentResultsQuery.where('type', '==', type);
        }
        try {
            return await this.firebase.firestore.fetchOnceArray(paymentResultsQuery);
        } catch (error: any) {
            this.logFirestoreError('failed to fetch payment results', error);
            return [];
        }
    }

    subscribeToPaymentStatistics(subscriber: (statistics: {[uid: string]: PaymentStatisticsDto}) => void) {
        return this.firebase.firestore.subscribeToCollection<PaymentStatisticsDto>(
            this.firebase.firestore.collectionRef<PaymentStatisticsDto>('/paymentStatistics'),
            (statistics) => subscriber(statistics || {})
        );
    }

    subscribeToPaymentStatisticsForUser = (
        uid: string,
        subscriber: (statistics: PaymentStatisticsDto | undefined) => void
    ) => {
        return this.firebase.firestore.subscribeToDocument<PaymentStatisticsDto>(
            this.firebase.firestore.documentRef(`/paymentStatistics/${uid}`),
            subscriber
        );
    };

    async fetchItemsPaymentsByIdsLocal(itemsIds: Array<string | number>): Promise<ItemPaymentDto[]> {
        const findByKey = async (key: string | number): Promise<WithKey<ItemPaymentDto>[]> => {
            const snapshots = await this.firebase.firestore.fetchOnceDocuments<ItemPaymentDto>(
                this.firebase.firestore.collectionRef(`/items/${key}/paymentResults`)
            );
            return toArrayWithKey<ItemPaymentDto>(snapshots);
        };
        const results = await Promise.all(itemsIds.map((itemId) => findByKey(itemId)));
        return results.reduce((a, i) => [...a, ...i], []);
    }
}
